/**
 * InfoHint — a small accessible "?" help button that reveals a short,
 * plain-language explanation on click (and keyboard focus).
 *
 * Used to demystify security jargon (passkeys, MFA, authenticator apps) for
 * users who aren't security experts, without cluttering the main UI. The hint
 * text is always available to screen readers via the toggle button's popover.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

interface InfoHintProps {
  /** Accessible label for the trigger, e.g. "What is a passkey?" */
  label: string
  /** The explanation shown in the popover. */
  children: React.ReactNode
}

export default function InfoHint({ label, children }: InfoHintProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)
  const popRef = useRef<HTMLSpanElement>(null)
  const [shiftX, setShiftX] = useState(0)
  const popId = useId()

  /**
   * Nudge the popover back on-screen.
   *
   * It is centred on its trigger, so a trigger near either screen edge pushes
   * half the popover off the viewport — on a phone that is most triggers, and
   * clamping the *width* doesn't help because the problem is position. Measure
   * once on open and translate by however much is hanging off.
   */
  useLayoutEffect(() => {
    if (!open) {
      setShiftX(0)
      return
    }
    const el = popRef.current
    if (!el) return
    const GUTTER = 8
    // documentElement.clientWidth, NOT window.innerWidth: under mobile
    // emulation an overflowing page widens the layout viewport, so innerWidth
    // grows to match the overflow and reports that the popover fits. clientWidth
    // stays at the real viewport width.
    const viewport = document.documentElement.clientWidth
    const r = el.getBoundingClientRect()
    const overRight = r.right - (viewport - GUTTER)
    const overLeft = GUTTER - r.left
    if (overRight > 0) setShiftX(-overRight)
    else if (overLeft > 0) setShiftX(overLeft)
    else setShiftX(0)
  }, [open])

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      // Capture phase + stopPropagation so an open hint *claims* the Escape.
      // These hints sit inside dialogs (Profile → re-auth, MFA, passkey), and
      // the dialog's own Escape handler lives on an ancestor element, so a
      // plain bubbling listener here would let one Escape close the hint AND
      // the dialog around it. Innermost transient layer wins.
      e.stopPropagation()
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  return (
    <span ref={wrapRef} className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onClick={() => setOpen((v) => !v)}
        // The visible circle stays 16px, but the button box is 24px so it meets
        // the WCAG 2.5.8 minimum — the hit area grows, the design doesn't.
        // focus-visible, not focus: a plain `focus:` ring sticks to whichever
        // glyph a mouse last clicked, which Jesse called out as looking wrong.
        // Keyboard users still get a ring. primary-500 rather than -400 because
        // -400 is 2.3:1 on white and fails WCAG 2.4.11; -500 is 3.7:1 at the
        // same weight. Matches FieldHint.
        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-500 hover:text-gray-700 outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <span
          aria-hidden="true"
          className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-[10px] font-bold leading-none hover:bg-gray-100"
        >
          ?
        </span>
      </button>
      {open && (
        <span
          ref={popRef}
          id={popId}
          role="tooltip"
          // marginLeft, not transform: a transform is visual only, so the
          // element's layout box would still stick out and the page would still
          // scroll sideways even though the popover looked correct.
          style={{ marginLeft: shiftX }}
          className="absolute left-1/2 top-6 z-30 w-[min(16rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-md border border-gray-200 bg-white p-3 text-left text-xs font-normal leading-relaxed text-gray-600 shadow-lg"
        >
          {children}
        </span>
      )}
    </span>
  )
}
